import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import { useAuth } from "../../contexts/AuthContext";
import { useAlerts } from "../../contexts/AlertsContext";
import APIEndPoints from "../../utils/APIEndPoints";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import { IApiClientError } from "../../shared/ApiClient";
import APIError from "../../utils/APIError";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import Loading from "../common/Loading";
import FormContainer from "../common/FormContainer";
import { FaInfoCircle } from "react-icons/fa";
import FormInputText from "../common/FormInputText";
import FormTextArea from "../common/FormTextArea";
import CHElement from "../../model/CHElement";
import HeritageCategories from "../../model/enums/HeritageCategories";
import CHElementScale from "../../model/enums/CHElementScale";
import FormInputImage from "../common/FormInputImage";
import PagedData from "../../model/PagedData";
import FormDropDown from "../common/FormDropDown";
import FormInputNumber from "../common/FormInputNumber";
import UploadManager from "../../shared/UploadManager";
import UploadFileType from "../../model/enums/UploadFileType";
import ImgUtil from "../../utils/ImgUtil";
import FormInputCheckbox from "../common/FormInputCheckbox";
import AppSubSections from "../../utils/AppSubSections";
import Material from "../../model/Material";
import Country from "../../model/Country";
import SearchFilterAdministrative from "../search/SearchFilterAdministrative";
import MapModal from "../common/map/MapModal";
import AdministrativeStructure from "../../model/AdministrativeStructure";
import MapMarkerData from "../../model/MapMarkerData";
import FormInputRadioButton from "../common/FormInputRadioButton";
import { EXPLANATION_HEADER } from "../common/dialog_window/LicenseDialog";

interface IChElementFormErrors {
  name: string;
  description: string;
  cultural_sphere: string;
  id_site: string;
  id_country: string;
  start_year: string;
  end_year: string;
  author: string;
  address: string;
  latitude: string;
  longitude: string;
}

const defaultFormErrors: IChElementFormErrors = {
  name: "",
  description: "",
  cultural_sphere: "",
  id_site: "",
  id_country: "",
  start_year: "",
  end_year: "",
  author: "",
  address: "",
  latitude: "",
  longitude: "",
};

function ChElementAddEdit() {
  // read the ID from the url
  const { id } = useParams();
  const { sid } = useParams();

  // manage loading animation
  const [loading, setLoading] = useState(true);
  // manage the display of the map modal
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  // manage countries
  const [countries, setCountries] = useState<Country[]>([]);
  // manage pilots
  const [allMaterials, setAllMaterials] = useState<Material[]>([]);
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // set state for form info
  const [formData, setFormData] = useState<CHElement>(
    CHElement.EmptyInstance(),
  );
  // set state for form errors
  const [formErrors, setFormErrors] =
    useState<IChElementFormErrors>(defaultFormErrors);

  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the AuthManager
  const authManager = useAuth();
  // reference to the alerts system
  const alertsManager = useAlerts();
  // instance of UploadManager
  const [uploadManager] = useState(new UploadManager({ apiClient: apiClient }));
  // the list of selected materials
  const [selectedMaterialsIDs, setSelectedMaterialsIDs] = useState<number[]>(
    [],
  );
  // the ID of the Other material
  const [otherMaterialID, setOtherMaterialID] = useState<number | undefined>(
    undefined,
  );
  // the description of the Other material
  const [otherMaterialDescription, setOtherMaterialDescription] = useState("");

  // user agreement to terms and conditions
  const [agreeTerms, setAgreeTerms] = useState(false);

  // load record data from the server
  useEffect(() => {
    const getAllData = async () => {
      try {
        let chElement: CHElement | undefined = undefined;
        if (id) {
          chElement = await apiClient?.get<CHElement>({
            url: APIEndPoints.CHELEMENTS.ONE(parseInt(id)),
          });
        }
        const pagedMaterials = await apiClient?.get<PagedData<Material[]>>({
          url: APIEndPoints.MATERIALS.ALL,
        });

        // update local CHElement entries
        if (chElement) {
          chElement.local_country = chElement.country;
          chElement.local_province = chElement.province;
          chElement.local_municipality = chElement.municipality;
          chElement.start_year_bc = chElement.start_year < 0;
          chElement.end_year_bc = chElement.end_year < 0;
          chElement.start_year = Math.abs(chElement.start_year);
          chElement.end_year = Math.abs(chElement.end_year);
        }

        // set into the state all the information
        setFormData(chElement ? chElement : CHElement.EmptyInstance());
        setAllMaterials(pagedMaterials.items ?? []);
        setFormErrors(defaultFormErrors);
        setSelectedMaterialsIDs(
          chElement && chElement.materials
            ? chElement.materials.map((material) => material.id!)
            : [],
        );

        // get the ID & details of the Other material
        for (var i = 0; i < pagedMaterials.items.length; i++) {
          if (pagedMaterials.items[i].name?.trim().startsWith("Other")) {
            const auxID = pagedMaterials.items[i].id!;
            setOtherMaterialID(auxID);
            // display the information about Other materials, if it is the case
            setOtherMaterialDescription(
              chElement && chElement.materials
                ? chElement.materials.filter(
                    (material) => material.id === auxID,
                  )[0].description!
                : "",
            );
            console.log(pagedMaterials.items[i]);
            break;
          }
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // validate errors from the form
    const errors = validateSiteForm(formData);

    setFormErrors(errors);
    setErrorMessage(
      isFormInvalid(errors)
        ? "Some of the data input is invalid. Please check and try again."
        : "",
    );

    if (isFormInvalid(errors)) {
      return;
    }

    var iconServerId: number | undefined = id ? formData.icon : undefined;
    try {
      // upload the new icon, if it exists (situation managed by the UploadManager)
      const uploadResult = await uploadManager.uploadFiles();

      if (uploadResult instanceof Array) {
        iconServerId = uploadResult[0].serverId;
      }
    } catch (exception) {
      console.log(exception);
    }

    // compute materials list
    const materialsList: Material[] = [];
    for (var i = 0; i < selectedMaterialsIDs.length; i++) {
      const auxMaterial = new Material("", "", selectedMaterialsIDs[i]);
      if (selectedMaterialsIDs[i] === otherMaterialID) {
        auxMaterial.description = otherMaterialDescription;
      }
      materialsList.push(auxMaterial);
    }

    var body = new CHElement(
      authManager.getUserID()!!,
      formData.name,
      formData.description,
      sid ? parseInt(sid) : formData.id_site,
      formData.heritage_category,
      formData.start_year_bc ? -formData.start_year : formData.start_year,
      formData.end_year_bc ? -formData.end_year : formData.end_year,
      formData.approximate_date,
      iconServerId,
      formData.scale,
      formData.spatial_dimension,
      formData.address,
      formData.author,
      formData.cultural_sphere,
      formData.id_country,
      formData.id_province,
      formData.id_municipality,
      materialsList,
      formData.latitude,
      formData.longitude,
    );

    // check if we have EDIT or CREATE
    if (id) {
      body.id = parseInt(id);
      apiClient
        ?.put({
          url: APIEndPoints.CHELEMENTS.ONE(parseInt(id)),
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors); // Reset errors
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "CH Element updated successfully",
          });
          navigate(
            SETTINGS.go_back({
              section: AppSections.CHELEMENTS,
              id: parseInt(id),
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    } else {
      apiClient
        ?.post<CHElement>({
          url: APIEndPoints.CHELEMENTS.NEW,
          body: body,
        })
        .then((response) => {
          setFormErrors(defaultFormErrors);
          alertsManager.showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: "CH Element created successfully",
          });
          navigate(
            SETTINGS.go_back({
              section: AppSections.CHELEMENTS,
              id: response.id,
              subsection: AppSubSections.VIEW,
            }),
          );
        })
        .catch((error: IApiClientError) => {
          if (!error.processed) {
            processAPIErrors(error);
          }
        });
    }
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.CHELEMENTS.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Asset deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections.SITES,
            id: formData.id_site,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete Asset",
          });
        }
      });
  };

  const formInputChange = (
    inputID: string,
    newValue: number | string | boolean | undefined | Country,
  ) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));

    if (inputID === "other_material_description") {
      setOtherMaterialDescription(String(newValue));
    } else if (inputID === "scale") {
      if (newValue === CHElementScale.Building) {
        formDataCopy["heritage_category"] = HeritageCategories.Immovable;
      }
      formDataCopy[inputID] = newValue;
    } else if (
      inputID === "country" ||
      inputID === "province" ||
      inputID === "municipality"
    ) {
      formDataCopy["id_" + inputID] = (
        newValue as AdministrativeStructure
      )?.geonameid;
      formDataCopy["local_" + inputID] = newValue;
      // cascading the updates to the other sections
      const cascadeItems = [];
      if (inputID === "country") {
        cascadeItems.push("province");
        cascadeItems.push("municipality");
      }
      if (inputID === "province") {
        cascadeItems.push("municipality");
      }
      cascadeItems.map((item) => {
        formDataCopy["id_" + item] = undefined;
        formDataCopy["local_" + item] = undefined;
      });
    } else if (inputID === "start_year" || inputID === "end_year") {
      const auxNewValue = parseInt(newValue?.toString()!);
      if (auxNewValue < 0) {
        formDataCopy[inputID + "_bc"] = true;
        formDataCopy[inputID] = Math.abs(auxNewValue);
      } else {
        formDataCopy[inputID] = newValue;
      }
    } else {
      formDataCopy[inputID] = newValue;
    }
    setFormData(formDataCopy as CHElement);
  };

  const checkBoxListChange = (id: string, checked: boolean) => {
    const copyIDs = new Set(JSON.parse(JSON.stringify(selectedMaterialsIDs)));
    if (checked) {
      copyIDs.add(Number(id.split("_")[1]));
    } else {
      copyIDs.delete(Number(id.split("_")[1]));
    }
    const copyIDsArray = Array.from(copyIDs as Set<number>);
    setSelectedMaterialsIDs(copyIDsArray);
  };

  const formIconChange = (
    id: string,
    value: string | undefined,
    files: FileList | null,
  ) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy["icon_path"] = value;
    formDataCopy["icon"] = -1;
    uploadManager.replaceFiles(id, UploadFileType.ICON, files);
    setFormData(formDataCopy as CHElement);
  };

  const onFormCancel = () => {
    navigate(SETTINGS.go_back({ section: AppSections.CHELEMENTS }));
  };

  const processAPIErrors = (serverError: IApiClientError) => {
    try {
      // convert the error to ChemiNovaAPI custom errors
      const apiErrors = serverError.details.response.data as APIError[];
      const formErrorsCopy: any = defaultFormErrors;
      apiErrors.forEach((apiError) => {
        formErrorsCopy[apiError.loc[0]] = apiError.msg;
      });
      setFormErrors(formErrorsCopy);
      setErrorMessage(
        "Some of the data input is invalid. Please check the form and try again.",
      );
    } catch (exception) {
      setErrorMessage("Something went wrong. Please try again later!");
    }
  };

  const changeCoordinates = (marker: MapMarkerData) => {
    const formDataCopy = JSON.parse(JSON.stringify(formData));
    formDataCopy.latitude = marker.lat;
    formDataCopy.longitude = marker.lon;
    setFormData(formDataCopy as CHElement);
  };

  const heritageCategoryOptions = Object.entries(HeritageCategories).map(
    ([key, value]) => ({
      label: key.replace(/_/g, " "),
      value: value,
    }),
  );

  const pageTitle = id
    ? "Edit Cultural Heritage Asset"
    : "Add a new Cultural Heritage Asset";
  const confirmLabel = id
    ? "Update Cultural Heritage Asset"
    : "Add Cultural Heritage Asset";

  const deleteButton = id ? (
    <Button
      label="Delete this Asset"
      purpose={ButtonAction.DELETE}
      onClick={() => {
        alertsManager.showDialogWindow({
          type: DialogWindowType.CONFIRM,
          message:
            "Are you sure you want to delete this Cultural Heritage Asset?",
          cancelLabel: "Cancel",
          confirmLabel: "Yes, delete Asset",
          onConfirm: handleDelete,
        });
      }}
    />
  ) : null;

  const selectedMaterialsIDsSet = new Set(selectedMaterialsIDs);

  if (loading) {
    return <Loading />;
  }

  return (
    <FormContainer
      title={pageTitle}
      onSubmit={(e) => {
        handleSubmit(e);
      }}
      headButton={deleteButton}
      hint="asset"
    >
      <FormInputImage
        label="Picture"
        id="icon"
        placeholderImage=""
        value={ImgUtil.display_icon(formData.icon_path)}
        required={false}
        onChange={formIconChange}
      />

      <label className="form-label">Scale*</label>
      <div className="form-input-group" style={{ marginTop: "5px" }}>
        <FormInputRadioButton
          label=""
          details="Object"
          id="scale"
          value={CHElementScale.Object}
          selectedValue={formData.scale}
          required={false}
          onChange={formInputChange}
          hint="Refers to cultural heritage assets of small/medium size; both movable and immovable, such as pieces of collections, paintings, sculptures."
        />
        <FormInputRadioButton
          label=""
          details="Building"
          id="scale"
          value={CHElementScale.Building}
          selectedValue={formData.scale}
          required={false}
          onChange={formInputChange}
          hint="Refers to cultural heritage assets of large size, immovable such as monuments and historical buildings and parts of building."
        />
      </div>

      <FormInputText
        label="Name"
        id="name"
        value={formData.name}
        required={true}
        placeholder="Name of Cultural Heritage Asset"
        onChange={formInputChange}
        error={formErrors.name}
      />

      <FormInputText
        label={
          formData.scale === CHElementScale.Building ? "Creator" : "Author"
        }
        id="author"
        value={formData.author}
        required={false}
        placeholder=""
        onChange={formInputChange}
        error={formErrors.author}
      />

      <FormTextArea
        label="Description"
        id="description"
        value={formData.description}
        required={true}
        onChange={formInputChange}
        error={formErrors.description}
      />

      <FormTextArea
        label="Cultural Sphere"
        id="cultural_sphere"
        value={formData.cultural_sphere}
        required={false}
        onChange={formInputChange}
        error={formErrors.cultural_sphere}
        hint="Refers to the broader cultural, historical, artistic, and social context to which a particular cultural heritage asset belongs."
      />

      <div>
        <label className="form-label">Administrative information*</label>
        <div className="form-input-group-vertical">
          <SearchFilterAdministrative
            onChange={formInputChange}
            selectedCountry={formData.local_country}
            selectedProvince={formData.local_province}
            selectedMunicipality={formData.local_municipality}
            selectTopCSS="select-search-addedit"
          />
        </div>
        {formErrors.id_country != "" && (
          <div className="form-error">
            Please give some administrative information about the asset.
          </div>
        )}
      </div>

      <FormInputText
        label="Address"
        id="address"
        value={formData.address}
        required={false}
        placeholder="Address of CH Element"
        onChange={formInputChange}
        error={formErrors.address}
      />

      <FormDropDown
        label="Heritage category"
        id="heritage_category"
        value={formData.heritage_category}
        required={true}
        options={heritageCategoryOptions}
        onChange={formInputChange}
        disabled={formData.scale === CHElementScale.Building}
      />

      <label className="form-label">Creation date*</label>
      <div className="form-input-group-vertical">
        <div className="flex w-full items-stretch">
          <div className="flex-grow mr-4">
            <FormInputNumber
              label="Start year"
              id="start_year"
              value={formData.start_year}
              required={true}
              onChange={formInputChange}
              error={formErrors.start_year}
            />
          </div>
          <div className="flex-grow pt-8">
            <FormInputCheckbox
              label=""
              details="before Christ"
              id="start_year_bc"
              checked={formData.start_year_bc}
              required={false}
              onChange={formInputChange}
            />
          </div>
        </div>
        <div className="flex w-full items-stretch">
          <div className="flex-grow mr-4">
            <FormInputNumber
              label="End year"
              id="end_year"
              value={formData.end_year}
              required={true}
              onChange={formInputChange}
              error={formErrors.end_year}
            />
          </div>
          <div className="flex-grow pt-8">
            <FormInputCheckbox
              label=""
              details="before Christ"
              id="end_year_bc"
              checked={formData.end_year_bc}
              required={false}
              onChange={formInputChange}
            />
          </div>
        </div>
        <div className="flex w-full items-stretch">
          <div className="flex-grow">
            <FormInputCheckbox
              label=""
              details="years mentioned above are aproximate"
              id="approximate_date"
              checked={formData.approximate_date}
              required={false}
              onChange={formInputChange}
            />
          </div>
        </div>
      </div>

      <label className="form-label">Select materials</label>
      <div className="form-input-group" style={{ marginTop: "5px" }}>
        {allMaterials.map((material) => {
          return (
            <FormInputCheckbox
              key={"ficb_" + material.id}
              label={""}
              details={Material.Display(material)}
              id={"material_" + material.id}
              checked={selectedMaterialsIDsSet.has(material.id!)}
              required={false}
              onChange={checkBoxListChange}
              css_label="form-label-inline"
            />
          );
        })}

        {selectedMaterialsIDsSet.has(otherMaterialID!) && (
          <div className="col-span-2 mx-6 mt-2">
            <FormInputText
              label="Details on other materials"
              id="other_material_description"
              value={otherMaterialDescription}
              required={false}
              placeholder=""
              onChange={formInputChange}
            />
          </div>
        )}
      </div>

      {isMapModalOpen && (
        <MapModal
          title="Input Geographical Coordinates of Cultural Heritage Asset"
          data={[formData]}
          onModalClose={() => setIsMapModalOpen(false)}
          onUpdateLocation={changeCoordinates}
          isInputEnabled={true}
        />
      )}

      <div className="flex w-full">
        <div className="flex-grow mr-4">
          <FormInputText
            label="Latitude"
            id="latitude"
            value={String(formData.latitude)}
            required={true}
            onChange={formInputChange}
            error={formErrors.latitude}
          />
        </div>
        <div className="flex-grow ml-4">
          <FormInputText
            label="Longitude"
            id="longitude"
            value={String(formData.longitude)}
            required={true}
            onChange={formInputChange}
            error={formErrors.longitude}
          />
        </div>
        <div className="flex-grow ml-4">
          <Button
            label="Open map"
            purpose={ButtonAction.MAP}
            onClick={() => setIsMapModalOpen(true)}
            className="w-full mt-8"
          />
        </div>
      </div>

      {errorMessage && (
        <div className="form-submit-error">
          <FaInfoCircle />
          <div>{errorMessage}</div>
        </div>
      )}

      {/* License Agreement group */}
      <div className="form-input-group mt-4 flex flex-col gap-2 pl-4">
        <label className="text-sm">{EXPLANATION_HEADER}</label>

        <label className="flex items-center gap-2 text-sm mt-1">
          <input
            type="checkbox"
            checked={agreeTerms}
            onChange={(e) => setAgreeTerms(e.target.checked)}
          />
          <span>
            I agree with the{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                alertsManager.showDialogWindow({
                  type: DialogWindowType.LICENSE,
                  message: "license",
                });
              }}
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Terms and Conditions
            </a>
          </span>
        </label>
      </div>

      <div className="form-buttons-container">
        <Button
          label="Cancel"
          purpose={ButtonAction.CANCEL}
          onClick={() => onFormCancel()}
          className="w-full"
        />

        <Button
          label={confirmLabel} // "Add Cultural Heritage Asset" or "Update …"
          purpose={ButtonAction.SAVE}
          isSubmit={true}
          disabled={!agreeTerms}
          // activeState={1}
          className="w-full"
        />
      </div>
    </FormContainer>
  );
}

function validateSiteForm(formData: CHElement): IChElementFormErrors {
  let errors: IChElementFormErrors = {
    name: "",
    description: "",
    cultural_sphere: "",
    id_site: "",
    start_year: "",
    end_year: "",
    address: "",
    author: "",
    id_country: "",
    latitude: "",
    longitude: "",
  };

  errors.name =
    formData.name.length < 4
      ? "Cultural Heritage Asset name should be at least 4 characters long"
      : "";
  errors.description =
    formData.description.length < 10
      ? "Cultural Heritage Asset description should be at least 10 characters long"
      : "";
  /*errors.start_year =
    formData.start_year < -5000 ? "Start year should be at least 5000 AD" : "";
  errors.end_year =
    formData.end_year > 2024
      ? "End year should be maximum the current year"
      : "";
  errors.address =
    formData.address.length < 4
      ? "The address should be at least 4 characters long"
      : "";
  errors.author =
    formData.author.length < 4
      ? "The Author name should be at least 4 characters long"
      : "";
  errors.id_country =
    formData.id_country < 1
      ? "Please select the country where the Cultural Heritage Asset is located"
      : "";
  errors.latitude = !formData.latitude
    ? "Please provide geographical coordinates of the site"
    : formData.latitude > 90 || formData.latitude < -90
    ? "Valid values are between -90 and 90"
    : "";
  errors.longitude = !formData.longitude
    ? "Please provide geographical coordinates of the site"
    : formData.longitude > 180 || formData.longitude < -180
    ? "Valid values are between -180 and 180"
    : "";*/

  return errors;
}

function isFormInvalid(errors: IChElementFormErrors): boolean {
  return (
    errors.name !== "" ||
    errors.description !== "" ||
    errors.id_site !== "" ||
    errors.start_year !== "" ||
    errors.end_year !== "" ||
    errors.address !== "" ||
    errors.author !== "" ||
    errors.id_country !== "" ||
    errors.latitude !== "" ||
    errors.longitude !== ""
  );
}

export default ChElementAddEdit;
